'use client';

import { useState, FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { API_BASE_URL } from '@/lib/api-client';

export default function SupportPage() {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const { showToast } = useToast();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ name, email, message }),
      });

      const data = await response.json();

      if (response.ok) {
        showToast("Message sent. We'll get back to you within one business day.");
        setName('');
        setEmail('');
        setMessage('');
      } else {
        throw new Error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : 'Failed to send message. Please try again.',
        5000,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-16 sm:px-6">
      <p className="overline">Support</p>
      <h1 className="mt-2 font-serif text-display-sm font-semibold text-ink">
        Get in touch
      </h1>
      <p className="mt-3 max-w-prose text-body-lg text-muted">
        Questions about coverage, a plan, or API access? Send us a note and we&apos;ll
        reply within one business day.
      </p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5 panel p-6 sm:p-8">
        <div className="space-y-1.5">
          <label htmlFor="name" className="field-label">
            Name
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="field"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="email" className="field-label">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="field"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="message" className="field-label">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            rows={6}
            required
            minLength={10}
            maxLength={5000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="How can we help?"
            className="field resize-y"
          />
        </div>

        <Button type="submit" size="lg" disabled={loading} className="w-full">
          {loading ? 'Sending…' : 'Send message'}
        </Button>
      </form>

      <p className="mt-6 text-center text-body-sm text-muted">
        Or email us at{' '}
        <a href="mailto:support@prosaplatform.com" className="text-primary hover:underline">
          support@prosaplatform.com
        </a>
        .
      </p>
    </div>
  );
}
